/**
 * Bingo — deterministic seeding.
 *
 * The whole social premise of the game ("today's game, the same one everyone's
 * playing") rests on this being reproducible. Two things derive from a seed:
 *   1. the day's CALL SEQUENCE — a shuffled 1..75 order, the same for everyone
 *      in a community on a given day;
 *   2. the player's CARDS — valid 75-ball cards generated from the same seed,
 *      offset per card index so card #2 differs from card #1.
 *
 * We never touch `Math.random` on those paths. A 32-bit `mulberry32` PRNG seeded
 * from either a date stamp (the daily game) or a fresh random number (practice)
 * drives every shuffle and column draw. Same seed in → same game out.
 */
/** A small, fast, well-distributed 32-bit PRNG. Returns floats in [0, 1). */
export function mulberry32(seed) {
    let a = seed >>> 0;
    return function next() {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** FNV-1a hash of a string → a 32-bit unsigned seed (never 0). */
export function seedFrom(key) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < key.length; i += 1) {
        h ^= key.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0 || 1;
}
/**
 * Today's local-date stamp, `YYYYMMDD`. Refreshes at local midnight, matching
 * how residents experience "today".
 */
export function todayStamp(date = new Date()) {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}${m}${d}`;
}
/** The daily seed — same for everyone in a community today. */
export function dailySeed(date = new Date()) {
    return seedFrom(`bingo-${todayStamp(date)}`);
}
/** A fresh, never-repeating practice seed. */
export function freshSeed() {
    return (Math.floor(Math.random() * 0xffffffff) >>> 0) || 1;
}
/**
 * Fisher–Yates shuffle driven by a supplied RNG. Pure: returns a new array,
 * leaves the input untouched. Same RNG sequence in → same permutation out.
 */
export function shuffle(items, rng) {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        const tmp = out[i];
        out[i] = out[j];
        out[j] = tmp;
    }
    return out;
}
//# sourceMappingURL=rng.js.map